import { useGetUserNameWithFallback } from '@pba/react-utils';
import type { ContentObjectList } from 'models';
import { type KeyboardEvent, useState } from 'react';
import { Avatar, Col, Flex, Image, type ModalOptions, NavigationPill, Paragraph, Row } from 'ui';
import { useContentObjectPill } from '../../hooks';
import { PinnedFeedCardLayout } from '../pinned-feed-card-layout';
import { useGetPinnedPostPreview } from './hooks';
import { PinnedPostCardModal, type PinnedPostCardModalOptions } from './pinned-post-card-modal';

type P = {
  item: ContentObjectList;
};

const PinnedPostCard = ({ item }: P) => {
  const getPinnedPostPreview = useGetPinnedPostPreview();
  const getUserNameWithFallback = useGetUserNameWithFallback();
  const [modalOptions, setModalOptions] = useState<ModalOptions<PinnedPostCardModalOptions>>({ isOpened: false });
  const pill = useContentObjectPill({ isMemberOfProgram: false, item });
  const { imageUrl, text } = getPinnedPostPreview(item);

  const openModal = () => {
    setModalOptions({ isOpened: true, item });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    openModal();
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/prefer-tag-over-role -- card must stay a div so browser button styles do not center the content */}
      <div className="pinned-feed-post-card" onClick={openModal} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
        <PinnedFeedCardLayout className="pinned-feed-post-card__layout">
          <Flex className="pinned-feed-post-card__content" gap="small" vertical>
            {/* TODO: #87927 This author and pill row exists only here. ContentObjectHeader is the full post header (name, date, bookmark, menu) and does not fit this card. Extract a compact shared row when a second screen needs the same row. */}
            <Row align="middle" gutter={['xs', 0]} wrap={false}>
              <Col>
                <Avatar size={24} src={item.author.profilePhoto}>
                  {getUserNameWithFallback(item.author)}
                </Avatar>
              </Col>
              {pill ? (
                <Col flex="auto">
                  <NavigationPill fallbackPhoto={pill.fallbackPhoto} name={pill.name} photoUrl={pill.photoUrl} />
                </Col>
              ) : null}
            </Row>
            {text ? (
              <Paragraph ellipsis={{ rows: 5 }}>
                <>{text}</>
              </Paragraph>
            ) : null}
            {text || !imageUrl ? null : (
              <div className="pinned-feed-post-card__image">
                <Image borderRadius="small" layout="fluid-image" objectFit="cover" preview={false} src={imageUrl} />
              </div>
            )}
          </Flex>
        </PinnedFeedCardLayout>
      </div>
      <PinnedPostCardModal
        onCancel={() => {
          setModalOptions({ isOpened: false });
        }}
        options={modalOptions}
      />
    </>
  );
};

export default PinnedPostCard;
